import * as React from "react";
import {cx}       from "emotion";

import "./text-area-field.scss";

import {TextareaAutosize}         from "@Common/libs/react-autosize-textarea/textarea-autosize";
import {bindInput}                from "@Common/react/bind-input";
import {consumeContext}           from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}                from "@Common/react/cst2";
import {Load2}                    from "@Common/react/load2";
import {Static2}                  from "@Common/react/static-2";
import {UseRef}                   from "@Common/react/use-ref";
import {UseState}                 from "@Common/react/use-state";
import {Button}                   from "@Common/tools/cc-integration/ui/button/button";
import {Types}                    from "@Common/types";
import {ButtonSpinner}            from "@Common/ui-components/loading-spinners/button-spinner";
import {QuillTextEditor}          from "@Common/ui-components/quill-text-editor/quill-text-editor";
import {TextAreaContentOptions}   from "@Common/ui-components/text-area-content-options/text-area-content-options";

import {bindInputMask} from "./bind-input-mask";
import {getInfoTip}    from "../common/get-info-tip";
import {FieldOptions}  from "@Common/ui-components/field-options/fieldOptions";


export const TextAreaField = ({
    textField,
    cc,
    xdQaName,
    allowSuggestedComments = false,
    variableData           = null,
    itemId                 = null,
    isAPM                  = false,
}:{
    textField                : Types.Common.Tools.CcIntegration.Models.TextField,
    cc                       : Types.Common.Tools.CcIntegration.Models.CcEditor,
    xdQaName                 : string,
    allowSuggestedComments   : boolean,
    variableData            ?: Types.Apis.Models.VariableData[],
    itemId                   : string|null
    isAPM                   ?: boolean
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("company"))

    .then("textAreaDetails", ({}, next:ComponentLink<typeof Load2<any>>) => Load2({
        fetch: async () => {
            let product = await cc.editor.getProduct();
            let details = await product.getItemById(textField.id)
            return details;
        },
        next
    }))

    .then("allowOverride", ({textAreaDetails}, next:Link<JSX.Element, boolean>) => next(
        // 0: "clip", 1: "fit to width", 2: "fit to box"
        textAreaDetails.value?._overflowStrategy != 0
    ))

    .then("fontSize", ({textAreaDetails}, next:Link<JSX.Element, number>) => next(
        textAreaDetails.value?._font._size
    ))

    .then("areaOfTextbox", ({textAreaDetails}, next:Link<JSX.Element, number>) => next(
        textAreaDetails.value?._textRectangle?._width * textAreaDetails.value?._textRectangle?._height
    ))

    .then("estimatedMaxCharacters", ({allowOverride, fontSize, areaOfTextbox}, next:Link<JSX.Element, number>) => next(
        // brute force analysis shows you need about 8.5sq pts of text area for each pnt size of font for a single character
        // We should keep an eye on this & update if this estimate needs to be tweaked.
        allowOverride ? Math.floor( (areaOfTextbox / fontSize) / 8.5 ) : Math.floor( ( (areaOfTextbox / fontSize) / 8.5 ) * .95 )
    ))

    .then("aiTextGenerating", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({
        initValue: false,
        next
    }))

    .then("aiBlockedByBindings", ({}, next:Link<JSX.Element, boolean>) => next(
        ["xd.user", "xd.office", "xd.company", "xd.region", "xd.listing.listedPrice"].some(substring =>
            cc?.ccConfig?.templateBindingData[textField.side]?.[textField.name]?.value?.includes(substring))
    ))

    .then("enableAiTools", ({estimatedMaxCharacters, allowOverride, aiBlockedByBindings, textAreaDetails, company}, next:Link<JSX.Element, boolean>) => next(
        company.prefs.suggestedCommentsMode !== 0 &&
        !textAreaDetails.value?._isRichText &&
        !textAreaDetails.value?._visualizationPermissions._noShow &&
        !aiBlockedByBindings &&
        // Might have to play with these numbers a bit
        // leaving code below even though this code could be written simply as "estimatedMaxCharacters > 50"
        ( (estimatedMaxCharacters > 50 && allowOverride) || estimatedMaxCharacters > 50 )
    ))

    .then("showAiIcon", ({enableAiTools}, next:ComponentLink<typeof UseState<boolean>>) => UseState({
        _key: enableAiTools,
        initValue: !!enableAiTools,
        next
    }))

    // create the textarea ref hook for finding focus caret position
    .then("textFieldRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext|any>) => UseRef({next}))

    // hold the quill current selection position
    .then("selectionPosition", (_, next:Link<
        JSX.Element,
        Types.Common.React.Static2.Static2Next<Types.Common.UiComponents.QuillTextEditor.TextFieldSelectionPosition>
    >) => Static2({
        getInitValue: () => ({
            editorStartPosition: 0,
            editorSelectedLength: 0,
            valueStartPosition: 0,
            valueSelectedLength: 0,
            valueLength: 0,
            hasFocus: false,
        }),
        next
    }))

    .then("mergeFieldOptions", ({textFieldRef, selectionPosition}, next:Link<
        JSX.Element,
        Types.Common.UiComponents.textAreaField.mergeFieldOptions
    >) => next(
        !!variableData?.length && !textField.inputMask && {
            variableData: variableData,
            onRecordSelectionRange: () => {

                // skip if not using a regular text area - selection range is always available for quill
                if (textFieldRef?.getEditor) return;

                // check if text area is active and record selection positions if found
                const relatedTextArea = textFieldRef.get() as HTMLTextAreaElement;

                if (relatedTextArea === document.activeElement) {
                    selectionPosition.set({
                        ...selectionPosition.get(),
                        editorStartPosition: relatedTextArea.selectionStart,
                        editorSelectedLength: relatedTextArea.selectionEnd - relatedTextArea.selectionStart,
                        valueStartPosition: relatedTextArea.selectionStart,
                        valueSelectedLength: relatedTextArea.selectionEnd - relatedTextArea.selectionStart,
                        hasFocus: true,
                    });
                }
                else {
                    selectionPosition.set({
                        ...selectionPosition.get(),
                        hasFocus: false,
                    });
                }

            },
            onSetValue: (mergeField) => {

                let newValue;
                const selection = selectionPosition.get();

                // Return focus to field after user selects merge Tag to place in field
                selectionPosition.set({
                    ...selection,
                    hasFocus: true,
                });

                // place at caret or over selected text if field has focus
                // Note: This was previously set to only fire if selection.hasFocus was true. Since adding tools to the
                //       text frame that is only shown when the field has focus, we can assume that the field always has
                //       focus. Changed to only place the merge field at the end if the selection information is not
                //       available.
                if (selection) {

                    let insertPositionStart = selection.valueStartPosition;
                    let insertPositionEnd = insertPositionStart + selection.valueSelectedLength;
                    let indexOffset = 0;

                    // for mergeTags in selection.mergeTagPositions if index (tag[0]) is within the selection range, add tag length (tag[1]) to the insertPositionEnd
                    // this accounts for the fact that the highlighted stylized tags are counted as 1 character, but we're working with the plain text value
                    selection.mergeTagPositions.forEach(tag => {
                        let tagValueIndex = tag[0] + indexOffset;
                        if (tagValueIndex >= (insertPositionStart) && tagValueIndex < (insertPositionEnd)) {
                            insertPositionEnd += (tag[1] - 1);
                        }
                        else if (tagValueIndex < (insertPositionStart)) {
                            indexOffset += (tag[1] - 1);
                        }
                    });

                    // set the new value with merge field between the selection points
                    newValue = textField.value.substring(0, insertPositionStart)
                        + mergeField
                        + textField.value.substring(insertPositionEnd, textField.value.length);

                }

                // add field to the end if no selection
                else {

                    newValue = textField.value;

                    // if there is a value that doesn't end in whitespace, automatically add a space to separate the tag from existing text
                    if (textField.value && ![" ", "\t", "\r", "\n"].includes(textField.value.at(-1))) newValue += " ";

                    // add the field to the end of the text
                    newValue += mergeField;

                }

                // apply the text change
                textField.onChange(newValue);

            },
        }
    ))

    .then(({aiTextGenerating, enableAiTools, showAiIcon, allowOverride, estimatedMaxCharacters, company, textFieldRef, selectionPosition, mergeFieldOptions}) => (
        <div className={cx("text-area-field-6j7 relative", {error: (textField.isRequired && !textField.value)})}>
            {textField.isRichText && textField.textPermissions.allowChangeText ? <>
                {/*Temporary block, will investigate compatibility with Quill text editor for long term solution.*/}
                <Button
                    buttonText={"SELECT IN CANVAS"}
                    xdQaName={"SELECT IN CANVAS"}
                    className="select-in-canvas"
                    onClick={async ()=> {
                        await cc.editor.selectItems([{'name':textField.name}]);
                    }}
                />
            </> : <>
                {aiTextGenerating.value && <div className="ai-text-loading-overlay"><ButtonSpinner size={50} spinnerColor="white"/></div>}
                <FieldOptions
                    reference={textFieldRef}
                    fieldId={textField.id}
                    idOrder={itemId}
                    fieldData={{
                        idPrimary: textField.id,
                        fieldKey: 27,
                        displayLabel: textField.label,
                    }}
                    allowAiTools={enableAiTools}
                    aiOptions={enableAiTools && {
                        currentValue: textField.value,
                        maxChars: estimatedMaxCharacters,
                        allowMaxCharsOverride: allowOverride,
                        onSetValue: (value) => {
                            textField.onChange(value);
                        },
                        onSetLoading: aiTextGenerating.onChange,
                    }}
                    allowSuggestedComments={allowSuggestedComments && [0,2].includes(company.prefs.suggestedCommentsMode)}
                    charOverrideModalOptions={{
                        showCharCountModal: null,
                        modalMessage: null,
                        okCallback: null,
                        cancelCallback: null,
                        cancelButton: null,
                        enableLimitOverride: allowOverride,
                        onSetValue: (value) => {
                            textField.onChange(value);
                        },

                    }}
                    allowVdp={variableData?.length}
                    mergeFieldOptions={mergeFieldOptions}
                    ccField={true}
                    isAPM={isAPM}
                >
                    {/* use Quill editor for fields that accept a stylized variable data chip in place of the variable data tag */}
                    {variableData?.length ? (<>
                        <QuillTextEditor
                            value={textField.value}
                            fieldId={textField.id}
                            inputMask={textField.inputMask}
                            setRef={el => textFieldRef = el}
                            onChange={async value => textField.onChange(value)}
                            onFocusCallback={() => {
                                if(!enableAiTools) return;
                                showAiIcon.onChange(false);
                            }}
                            onBlurCallback={() => {
                                if(!enableAiTools) return;
                                showAiIcon.onChange(true);
                            }}
                            selectionPosition={selectionPosition}
                            variableData={variableData}
                            highlightInvalidMergeFields={false}
                            xdQaName={xdQaName}
                        />

                    </>) : (<>
                        <TextareaAutosize {...{
                            "id": textField.id,
                            "xd-qa-type": "textarea",
                            "xd-qa-name": xdQaName,
                            "data-hj-whitelist": "",
                            ref: (ref) => textFieldRef.set(ref),
                            onFocus:() => {
                                if(!enableAiTools) return;
                                showAiIcon.onChange(false);
                            },
                            onBlur:() => {
                                if(!enableAiTools) return;
                                showAiIcon.onChange(true);
                            },
                            style: {
                                resize: "vertical",
                            },
                            ...!textField.inputMask ? (
                                bindInput(textField)
                            ) : (
                                bindInputMask(textField, textField.inputMask)
                            )
                        }}/>
                        {(!!showAiIcon.value && !textField.value?.length && enableAiTools) ?
                            <div className="sparkle-icon"
                                onClick={() => textFieldRef.get()?.focus()}
                            />
                        : null}
                        {getInfoTip(textField, cc.sourceData)}

                    </>)}
                </FieldOptions>
            </>}
        </div>
    ))

    .run()

);
